import { PageLayout } from '../components/Layout';
import { 
  Shield, 
  Eye, 
  Warning, 
  Phone,
  Lock,
  UserCheck,
  VideoCamera,
  Flag
} from '@phosphor-icons/react';

const Safety = () => {
  const safetyFeatures = [
    {
      icon: UserCheck,
      title: "Verified Profiles",
      description: "Every member goes through photo verification to ensure authenticity."
    },
    {
      icon: VideoCamera,
      title: "In-Platform Video Calls",
      description: "First meetings happen within our secure platform with built-in safety features."
    },
    {
      icon: Lock,
      title: "Private Information",
      description: "Your contact details are never shared without mutual consent."
    },
    {
      icon: Flag,
      title: "Report Function",
      description: "Instantly report and block any user who makes you uncomfortable."
    }
  ];

  const onlineTips = [
    "Never share financial information with matches",
    "Don't send money to anyone you haven't met in person",
    "Keep conversations within our platform initially",
    "Trust your instincts—if something feels off, it probably is",
    "Report suspicious behavior immediately",
    "Take your time getting to know someone before meeting"
  ];

  const videoCallTips = [
    "Conduct your call in a comfortable, private space",
    "Let a friend or family member know about your scheduled call",
    "You can end the call at ANY time—no explanation needed",
    "Use our report feature if you experience inappropriate behavior",
    "Don't feel pressured to share personal details you're not ready to share"
  ];

  const meetingTips = [
    "Meet in a public place for your first in-person meeting",
    "Tell a friend or family member where you'll be",
    "Arrange your own transportation to and from the meeting",
    "Keep your phone charged and accessible",
    "Don't feel obligated to continue the date if you're uncomfortable",
    "Trust your gut feelings"
  ];

  return (
    <PageLayout>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-6">
              <Shield weight="fill" className="w-6 h-6 text-primary" />
              <span className="text-primary uppercase tracking-widest text-xs font-semibold">
                Your Safety Matters
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight">
              Safety Guidelines
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              At Link, Laugh & Love, your safety is our top priority. We've built our platform 
              with multiple safeguards and want to empower you with knowledge to stay safe.
            </p>
          </div>
        </div>
      </section>

      {/* Safety Features */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl md:text-4xl mb-12 text-center">
            Built-In Safety Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {safetyFeatures.map((feature, index) => (
              <div key={index} className="paper-card rounded-sm p-8 text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-6">
                  <feature.icon weight="light" className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-lg mb-3">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online Safety */}
      <section className="py-24 md:py-32 bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Eye weight="light" className="w-8 h-8 text-primary" />
                <h2 className="font-serif text-3xl">Online Safety Tips</h2>
              </div>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Protecting yourself online is the first step to a safe dating experience.
              </p>
              <ul className="space-y-4">
                {onlineTips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <VideoCamera weight="light" className="w-8 h-8 text-primary" />
                <h2 className="font-serif text-3xl">Video Call Safety</h2>
              </div>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Our video calls are designed to be safe, but here are extra tips.
              </p>
              <ul className="space-y-4">
                {videoCallTips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Meeting In Person */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <Warning weight="light" className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Meeting In Person
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              When you're ready to meet your match in person, keep these guidelines in mind.
            </p>
          </div>

          <div className="paper-card rounded-sm p-8">
            <ul className="space-y-4">
              {meetingTips.map((tip, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-primary">{index + 1}</span>
                  </div>
                  <span className="text-muted-foreground">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Report Section */}
      <section className="py-24 md:py-32 bg-stone-900 text-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Phone weight="light" className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            Need to Report Something?
          </h2>
          <p className="text-stone-400 mb-8 max-w-2xl mx-auto leading-relaxed">
            If you experience any inappropriate behavior, harassment, or feel unsafe, 
            please report it immediately. We take all reports seriously.
          </p>
          <div className="space-y-2 text-stone-300">
            <p>Use the report feature in your dashboard or video call</p>
            <p>Or email us directly at:</p>
            <a 
              href="mailto:LinkLaughLoveMatch@gmail.com" 
              className="text-primary font-medium text-lg hover:underline"
            >
              LinkLaughLoveMatch@gmail.com
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Safety;
